import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { LogIn } from 'lucide-react';

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await login(email, password);
      toast.success('Logged in successfully');
      navigate('/');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      <div 
        className="hidden lg:flex lg:w-1/2 relative bg-cover bg-center"
        style={{ backgroundImage: `url('https://images.unsplash.com/photo-1588630677000-523ab51ca0d3?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNTl8MHwxfHNlYXJjaHw0fHxhYnN0cmFjdCUyMHByb2Zlc3Npb25hbCUyMGNvcnBvcmF0ZSUyMGJhY2tncm91bmR8ZW58MHx8fHwxNzc0NDk1NjIxfDA&ixlib=rb-4.1.0&q=85')` }}
      >
        <div className="absolute inset-0 bg-gradient-to-br from-blue-900/90 to-slate-900/90"></div>
        <div className="relative z-10 p-12 flex flex-col justify-center">
          <h1 className="text-4xl font-semibold text-white mb-4 tracking-tight">Mestastop</h1>
          <p className="text-lg text-slate-200 leading-relaxed">Performance Appraisal System</p>
          <p className="text-sm text-slate-300 mt-4 leading-relaxed">Manage employee performance reviews with a comprehensive 2-level approval workflow.</p>
        </div>
      </div>

      <div className="w-full lg:w-1/2 flex items-center justify-center p-8 bg-[#F8FAFC]">
        <div className="w-full max-w-md">
          <div className="mb-8">
            <h2 className="text-2xl font-semibold text-slate-900 tracking-tight mb-2">Sign in to your account</h2>
            <p className="text-sm text-slate-700">Enter your credentials to access the appraisal system</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">
                Email Address
              </Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="border-slate-200 focus:ring-2 focus:ring-blue-600 focus:outline-none rounded-sm"
                data-testid="login-email-input"
                placeholder="you@company.com"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="password" className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">
                Password
              </Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="border-slate-200 focus:ring-2 focus:ring-blue-600 focus:outline-none rounded-sm"
                data-testid="login-password-input"
                placeholder="••••••••"
              />
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-[#1E40AF] hover:bg-[#1E3A8A] text-white rounded-sm transition-colors duration-200 ease-in-out"
              data-testid="login-submit-button"
            >
              {loading ? 'Signing in...' : (
                <>
                  <LogIn className="mr-2 h-4 w-4" />
                  Sign in
                </>
              )}
            </Button>
          </form>

          <div className="mt-6 p-4 border border-slate-200 bg-white rounded-sm">
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500 mb-3">Demo Credentials</p>
            <div className="space-y-2 text-sm text-slate-700">
              <p><strong>HR Admin:</strong> admin@mestastop.com / admin123</p>
              <p><strong>Manager:</strong> manager@mestastop.com / manager123</p>
              <p><strong>Employee:</strong> employee@mestastop.com / employee123</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
